import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { PyvServices } from '../../core/http/pyv-services/pyv.services';
import { ACCION_CLICK_MENU } from './auditoria-acciones.constants';
import {
  IAccionAuditoria,
  IAccionesPager,
  IFiltrosAccionesAuditoria,
  IJsMetadataAuditoria,
  IRegistrarAccionAuditoria,
  IResumenAccionesAuditoria,
} from './auditoria-acciones.interface';

@Injectable({
  providedIn: 'root',
})
export class AuditoriaAccionesService {

  constructor(private pyvServices: PyvServices) {}

  /**
   * Registra una acción de auditoría.
   * POST /admin/auditorias/acciones
   * @description jsMetadata es específico de cada txtAccion.
   */
  public registrarAccion(
    p_txtAccion: string,
    p_jsMetadata: IJsMetadataAuditoria
  ): Observable<IAccionAuditoria | null> {
    const body: IRegistrarAccionAuditoria = {
      txtAccion: p_txtAccion,
      jsMetadata: p_jsMetadata,
    };

    return this.pyvServices.post<IAccionAuditoria>('/auditorias/acciones', body).pipe(
      map((res) => res.data ?? null),
      catchError(() => of(null))
    );
  }

  /**
   * Registra un click de menú.
   * jsMetadata solo envía nomAccion (categoría -> opción).
   */
  public registrarClickMenu(
    p_jsMetadata: IJsMetadataAuditoria
  ): Observable<IAccionAuditoria | null> {
    return this.registrarAccion(ACCION_CLICK_MENU, p_jsMetadata);
  }

  /**
   * Consulta acciones de auditoría.
   * GET /admin/auditorias/acciones
   */
  public obtenerAcciones$(
    p_filtros: IFiltrosAccionesAuditoria = {}
  ): Observable<IAccionesPager> {
    const query = this.buildQuery(p_filtros);

    return this.pyvServices.get<IAccionesPager>(
      `/auditorias/acciones${query}`
    ).pipe(
      map((res) => {
        const pager = res.data ?? {
          pageData: [],
          totalRows: 0,
          page: 1,
          pages: 0,
        };

        return {
          ...pager,
          pageData: (pager.pageData ?? []).map(
            (accion) => this.mapearAccionConsulta(accion)
          ),
        };
      })
    );
  }

  /**
   * Consulta las series agregadas de clicks a menú.
   * GET /admin/auditorias/acciones-resumen
   */
  public obtenerResumen$(
    p_filtros: IFiltrosAccionesAuditoria = {}
  ): Observable<IResumenAccionesAuditoria> {
    const query = this.buildQuery({
      ...p_filtros,
      page: undefined,
      rows: undefined,
    });

    return this.pyvServices.get<IResumenAccionesAuditoria>(
      `/auditorias/acciones-resumen${query}`
    ).pipe(
      map((res) => res.data ?? {
        opciones: [],
        categorias: [],
        usuarios: [],
        totalClics: 0,
      })
    );
  }

  /**
   * Solicita la generación asíncrona del Excel de acciones.
   * GET /admin/auditorias/acciones-download
   */
  public descargarAcciones$(
    p_filtros: IFiltrosAccionesAuditoria = {}
  ): Observable<unknown> {
    const query = this.buildQuery({
      ...p_filtros,
      page: undefined,
      rows: undefined,
    });

    return this.pyvServices.get(`/auditorias/acciones-download${query}`);
  }

  /**
   * Prepara la fila de consulta: acción en crudo y detalle como JSON.
   */
  private mapearAccionConsulta(p_accion: IAccionAuditoria): IAccionAuditoria {
    return {
      ...p_accion,
      txtDetalle: JSON.stringify(p_accion.jsMetadata ?? {}),
    };
  }

  /**
   * Construye el query string a partir de los filtros.
   */
  private buildQuery(p_filtros: IFiltrosAccionesAuditoria): string {
    const params: string[] = [];

    if (p_filtros.txtNomUsuario) {
      params.push(`txtNomUsuario=${encodeURIComponent(p_filtros.txtNomUsuario)}`);
    }

    if (p_filtros.txtEmail) {
      params.push(`txtEmail=${encodeURIComponent(p_filtros.txtEmail)}`);
    }

    if (p_filtros.txtAccion) {
      params.push(`txtAccion=${encodeURIComponent(p_filtros.txtAccion)}`);
    }

    if (p_filtros.fecDesde) {
      params.push(`fecDesde=${encodeURIComponent(p_filtros.fecDesde)}`);
    }

    if (p_filtros.fecHasta) {
      params.push(`fecHasta=${encodeURIComponent(p_filtros.fecHasta)}`);
    }

    if (p_filtros.page != null) {
      params.push(`page=${p_filtros.page}`);
    }

    if (p_filtros.rows != null) {
      params.push(`rows=${p_filtros.rows}`);
    }

    return params.length ? `?${params.join('&')}` : '';
  }
}
